import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import moment from 'moment';
import { P2Alert, P2Button, P2ButtonGroup, P2Checkbox, P2Modal, P2TextDescription } from 'react-bootstrap-common-script';
import { Separator } from '../../../components/separator';
import PaymentMethod from '../../../components/payment-method/payment-method';
import { PolymerAxios } from '../../../utils/axios';
import {
  API_ApplicationCreditNotification,
  API_ValuationFeeBypassPaymentSuccess,
  API_ValuationFeePayment,
  API_ValuationFeeProcessPayment,
} from '../../../utils/api';
import { PaymentMethodEnum } from '../../../models/enum/payment-method.enum';
import config from '../../../config';
import FeeAndGst from '../../application-coo-stage-c/stage-c-fee-cost';
import { buildValuationFeeCreditLineAmounts } from './valuation-fee-credit-amounts';
import { useGetValuationFeeCheckUpdateStatusApiQuery } from '../../../redux/services/getValuationFeeCheckUpdateStatus';

export interface ValuationFeePendingDto {
  biome_request_valuation_log_id: number;
  application_uuid: string;
  status: string;
  due_date: string;
  valuation_fee: number;
}

export function isPayableValuationFeePending(p: ValuationFeePendingDto | null): boolean {
  if (!p) {
    return false;
  }
  return (p.status === 'PENDING' || p.status === 'SUBMIT_COMPLETE') && Number(p.valuation_fee) > 0;
}

type Props = {
  pending: ValuationFeePendingDto;
  applicationUuid: string;
  uinfin: string;
  singpassToken: string;
  onCancel: () => void;
  onRefresh: () => void;
};

type ApplicationPaymentResponseData = {
  status?: number;
  ttcNum?: string;
  systIdTagPayReq?: string;
  systIdforPaymentPayReq?: string;
  paymentUrl?: string;
  paymentStatus?: string;
  message?: string;
};

const PAYMENT_STATUS_COMPLETED = 'PAYMENT_COMPLETED';
const PAYMENT_STATUS_COMPLETE = 'PAYMENT_COMPLETE';
const PAYMENT_STATUS_ERROR = 'PAYMENT_ERROR';
const PAYMENT_STATUS_ERROR_RETRY_AVAILABLE = 'PAYMENT_ERROR_RETRY_AVAILABLE';
const PAYMENT_STATUS_IN_PROGRESS = 'PAYMENT_IN_PROGRESS';
const PAYMENT_STATUS_NO_EXISTING_PAYMENT = 'NO_EXISTING_PAYMENT';
const PAYABLE_STATUS_PENDING = 'PENDING';
const PAYABLE_STATUS_PAID = 'PAID';
const PAYABLE_STATUS_SUBMIT_COMPLETE = 'SUBMIT_COMPLETE';
const PAYMENT_IN_PROGRESS_MESSAGE = 'There is another payment in progress. Please try again later.';

/**
 * Applicant payment card for officer-initiated manual valuation fee (US2).
 * Shown on Stage D when POLYMER has an open PENDING / SUBMIT_COMPLETE payable row.
 */
export function StageDValuationFeeSection({ pending, applicationUuid, uinfin, singpassToken, onCancel, onRefresh }: Props) {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [paymentMethod, setPaymentMethod] = useState<PaymentMethodEnum>(PaymentMethodEnum.card);
  const [tncAccepted, setTncAccepted] = useState(false);
  const [payableStatus, setPayableStatus] = useState(pending.status);
  const [paymentStatusModalOpen, setPaymentStatusModalOpen] = useState(false);
  const [paymentStatusMessage, setPaymentStatusMessage] = useState('');
  const paymentFormRef = useRef<HTMLFormElement>(null);

  const isProd = useMemo(() => {
    if (
      process.env.NODE_ENV !== 'production' ||
      document.location.origin === 'https://services3-dev.hdb.gov.sg' ||
      document.location.origin === 'https://services3-stg.hdb.gov.sg'
    ) {
      return false;
    }
    return true;
  }, []);

  const paymentUrl = useMemo(() => {
    if (typeof document !== 'undefined' && document.location.origin === 'https://services3.hdb.gov.sg') {
      return config.prodPaymentURL;
    }
    return config.devAndStgPaymentURL;
  }, []);

  const paymentInProgress = payableStatus === PAYABLE_STATUS_SUBMIT_COMPLETE;
  const continueEnabled = tncAccepted && !loading && payableStatus === PAYABLE_STATUS_PENDING;
  const { data: checkUpdateStatusData, refetch: refetchCheckUpdateStatus } = useGetValuationFeeCheckUpdateStatusApiQuery(
    { application_uuid: applicationUuid, singpass_token: singpassToken, uinfin },
    { skip: !applicationUuid || !paymentInProgress }
  );

  useEffect(() => {
    setPayableStatus(pending.status);
  }, [pending.status]);

  useEffect(() => {
    if (paymentInProgress) {
      setPaymentStatusMessage(PAYMENT_IN_PROGRESS_MESSAGE);
      setPaymentStatusModalOpen(true);
    }
  }, [paymentInProgress]);

  useEffect(() => {
    if (!paymentInProgress) {
      return;
    }
    void refetchCheckUpdateStatus();
    const interval = window.setInterval(() => {
      void refetchCheckUpdateStatus();
    }, 5 * 60 * 1000);
    return () => window.clearInterval(interval);
  }, [paymentInProgress, refetchCheckUpdateStatus]);

  useEffect(() => {
    const status = checkUpdateStatusData?.status;
    if (status === PAYMENT_STATUS_COMPLETED || status === PAYMENT_STATUS_COMPLETE) {
      setPayableStatus(PAYABLE_STATUS_PAID);
      navigate(`/application-coo/stage-d/valuation-fee/success?application_id=${encodeURIComponent(applicationUuid)}`);
    }
    if (status === PAYMENT_STATUS_IN_PROGRESS) {
      setPayableStatus(PAYABLE_STATUS_SUBMIT_COMPLETE);
      setPaymentStatusMessage(PAYMENT_IN_PROGRESS_MESSAGE);
      setPaymentStatusModalOpen(true);
    }
    if (
      status === PAYMENT_STATUS_ERROR_RETRY_AVAILABLE ||
      status === PAYMENT_STATUS_NO_EXISTING_PAYMENT ||
      status === PAYMENT_STATUS_ERROR
    ) {
      setPayableStatus(PAYABLE_STATUS_PENDING);
      setPaymentStatusModalOpen(false);
      onRefresh();
    }
  }, [applicationUuid, checkUpdateStatusData?.message, checkUpdateStatusData?.status, navigate, onRefresh]);

  const createValuationFeePaymentSession = useCallback(async (): Promise<ApplicationPaymentResponseData> => {
    await PolymerAxios(uinfin, singpassToken).post(API_ValuationFeeProcessPayment, {
      application_uuid: applicationUuid,
    });
    const paymentResponse = await PolymerAxios(uinfin, singpassToken).post(API_ValuationFeePayment, {
      application_uuid: applicationUuid,
      payment_mode: paymentMethod,
    });
    return paymentResponse.data as ApplicationPaymentResponseData;
  }, [applicationUuid, uinfin, singpassToken, paymentMethod]);

  const handlePaymentInProgress = useCallback(() => {
    setPayableStatus(PAYABLE_STATUS_SUBMIT_COMPLETE);
    setPaymentStatusMessage(PAYMENT_IN_PROGRESS_MESSAGE);
    setPaymentStatusModalOpen(true);
  }, []);

  const handleAlreadyPaid = useCallback(
    (message?: string) => {
      setPayableStatus(PAYABLE_STATUS_PAID);
      setPaymentStatusMessage(message ?? 'Payment has been completed.');
      setPaymentStatusModalOpen(true);
      navigate(`/application-coo/stage-d/valuation-fee/success?application_id=${encodeURIComponent(applicationUuid)}`);
    },
    [applicationUuid, navigate]
  );

  const handlePaymentError = useCallback(
    (error: any, fallbackMessage: string) => {
      const status = error?.response?.status;
      const responseData = error?.response?.data;
      const responseText = typeof responseData === 'string' ? responseData : JSON.stringify(responseData ?? {});
      const message = responseData?.message ?? responseData?.error ?? responseData?.detail ?? fallbackMessage;

      if (status === 409 && responseText.includes('PAYMENT_IN_PROGRESS')) {
        handlePaymentInProgress();
        return;
      }
      if (status === 409 && responseText.includes('ALREADY_PAID')) {
        handleAlreadyPaid(message);
        return;
      }
      alert(fallbackMessage);
    },
    [handleAlreadyPaid, handlePaymentInProgress]
  );

  const buildBypassCreditNotificationBody = useCallback(
    (paymentData: ApplicationPaymentResponseData): Record<string, unknown> => {
      const ttcNum = paymentData?.ttcNum ?? '';
      const systIdforPayment = paymentData?.systIdforPaymentPayReq ?? 'BR20';
      const amounts = buildValuationFeeCreditLineAmounts(pending.valuation_fee);
      const details = amounts.map((amount, i) => ({
        bankReferenceNo: `BYPASS_REF_${i}`,
        receiptNum: `BYPASS_RCP_${i}`,
        caseNum: 'BYPCASE',
        systID: 'BYPASS_SYS',
        amount,
      }));
      return {
        systIdforPayment,
        numTrnscn: details.length,
        totalAmt: pending.valuation_fee,
        ttcNum,
        hashNum: 'BYPASS_HASH',
        transactionDate: moment().format('YYYY-MM-DD'),
        transactionTime: moment().format('HH:mm:ss'),
        details,
      };
    },
    [pending.valuation_fee]
  );

  const pay = useCallback(async () => {
    if (!applicationUuid || !tncAccepted || paymentInProgress) {
      return;
    }
    setLoading(true);
    try {
      const data = await createValuationFeePaymentSession();
      if (data?.paymentStatus === PAYMENT_STATUS_IN_PROGRESS) {
        handlePaymentInProgress();
        setLoading(false);
        return;
      }
      if (data?.paymentStatus === PAYMENT_STATUS_COMPLETED || data?.paymentStatus === PAYMENT_STATUS_COMPLETE) {
        handleAlreadyPaid(data.message);
        setLoading(false);
        return;
      }
      document.getElementById('vf-ttcNumPayReq')?.setAttribute('value', data?.ttcNum ?? '');
      document.getElementById('vf-systIdTagPayReq')?.setAttribute('value', data?.systIdTagPayReq ?? '');
      document.getElementById('vf-systIdforPaymentPayReq')?.setAttribute('value', 'BR20');
      setPayableStatus(PAYABLE_STATUS_SUBMIT_COMPLETE);
      setLoading(false);
      if (Number(pending?.valuation_fee) !== 0) {
        paymentFormRef.current?.submit();
      }
    } catch (e) {
      console.error('Valuation fee payment error', e);
      setLoading(false);
      handlePaymentError(e, 'Oops, something went wrong with payment. Please try again.');
    }
  }, [
    applicationUuid,
    createValuationFeePaymentSession,
    handleAlreadyPaid,
    handlePaymentError,
    handlePaymentInProgress,
    paymentInProgress,
    pending?.valuation_fee,
    tncAccepted,
  ]);

  const byPassPayment = useCallback(async () => {
    if (!applicationUuid || !tncAccepted || paymentInProgress) {
      return;
    }
    setLoading(true);
    try {
      const data = await createValuationFeePaymentSession();
      if (data?.paymentStatus === PAYMENT_STATUS_IN_PROGRESS) {
        handlePaymentInProgress();
        setLoading(false);
        return;
      }
      if (data?.paymentStatus === PAYMENT_STATUS_COMPLETED || data?.paymentStatus === PAYMENT_STATUS_COMPLETE) {
        handleAlreadyPaid(data.message);
        setLoading(false);
        return;
      }
      const ttcNum = data?.ttcNum;
      if (!ttcNum) {
        throw new Error('Missing ttcNum from valuation fee payment response');
      }
      const creditBody = buildBypassCreditNotificationBody(data);
      await PolymerAxios(uinfin, singpassToken).post(API_ApplicationCreditNotification, creditBody);

      const paymentSuccessParams = new URLSearchParams();
      paymentSuccessParams.append('ttcNumSuccess', ttcNum);
      paymentSuccessParams.append('AC28StrInputSuccess', 'BYPASS');
      paymentSuccessParams.append('AC28AmountSuccess', String(pending.valuation_fee));

      await PolymerAxios(uinfin, singpassToken, '', 'application/x-www-form-urlencoded').post(
        API_ValuationFeeBypassPaymentSuccess,
        paymentSuccessParams
      );

      setLoading(false);
      navigate(`/application-coo/stage-d/valuation-fee/success?application_id=${encodeURIComponent(applicationUuid)}`);
    } catch (e) {
      console.error('Valuation fee bypass payment error', e);
      setLoading(false);
      handlePaymentError(e, 'Oops, something went wrong with bypass payment. Please try again.');
    }
  }, [
    applicationUuid,
    buildBypassCreditNotificationBody,
    createValuationFeePaymentSession,
    handleAlreadyPaid,
    handlePaymentError,
    handlePaymentInProgress,
    navigate,
    pending.valuation_fee,
    paymentInProgress,
    singpassToken,
    tncAccepted,
    uinfin,
  ]);

  return (
    <div className='polymer-stage-d-valuation-fee'>
      <div className='polymer-stage-d-alert'>
        {pending?.status === 'PENDING' && pending?.due_date
          ? (
              <P2Alert
                variant='warning'
                text={`1 items pending your actions. Please respond by ${pending.due_date} or your application will be cancelled.`}
              />
            )
          : (
              <P2Alert
                variant='warning'
                text={`1 items pending your actions.`}
              />
            )
        }
 
      </div>
      <div className='polymer_sectioncard flex-fill polymer-stage-d-valuation-fee__section-card'>
        <div className='polymer_sectioncard__top'>
          <P2TextDescription title={'Application Updates'} />
        </div>
        <div className='polymer_sectioncard__bottom'>
          <div className='polymer-main-column-container'>
            <h2 className='polymer-stage-d-valuation-fee__payment-title'>Payment</h2>
            <p className='polymer-main-column-header polymer-stage-d-valuation-fee__intro'>
              Kindly note on the following fees required for HDB to process your application.
            </p>
            <div className='polymer-stage-d-valuation-fee__fees'>
              <div className='polymer-fees-container'>
                <div className='polymer-fees-header'>
                  <p className='polymer-fees-subheader'>Fees</p>
                </div>
                <Separator />
                <div className='polymer-fees-text'>
                  <div className='polymer-fees-gst-container'>
                    <p className='polymer-fees-700'>Valuation fee:</p>
                    <FeeAndGst fee_cost={Number(pending.valuation_fee)} />
                  </div>
                  <div className='polymer-fees-400 d-flex flex-column gap-4 polymer-stage-d-valuation-fee__fee-description'>
                    <div>
                      As the proposed owners have indicated for a new HDB mortgage loan for the change in flat ownership and the current flat is
                      bought from the open market, a current market valuation report is required for HDB to determine the mortgage loan ceiling.
                      Valuation fee is inclusive of GST and administration fee.
                    </div>
                    <div>
                      Please note that an appointed HDB panel valuer will be assigned to perform the valuation of your flat. By proceeding with the
                      payment, you agree and consent to share your contact information with the appointed valuer. The valuer will contact the owners
                      to make an appointment to conduct the valuation.
                    </div>
                  </div>
                </div>
                <Separator />
                <div className='polymer-fees-total-container'>
                  <p className='polymer-fees-700'>Total:</p>
                  <FeeAndGst fee_cost={Number(pending.valuation_fee)} />
                </div>
              </div>
            </div>

            <PaymentMethod PaymentHandler={(value) => setPaymentMethod(value as PaymentMethodEnum)} selectedPaymentMethod={paymentMethod} />
            <div className='polymer-main-column-spacer'>
              <Separator />
            </div>
            <div className='polymer-main-column-tnc polymer-stage-d-valuation-fee__tnc'>
              <p className='polymer-main-column-tnc-content polymer-stage-d-valuation-fee__tnc-content'>
                All fees paid are non-refundable in the event of any cancellation or rejection of the application. This includes the cancellation of
                the application, where submission of information or documents was not completed within the specified timeframe, thus rendering the
                application incorrect or incomplete. For updates on the application progress, you may check our progress tracker once the application
                is submitted successfully. You may refer to the Status Update to view updates on your application once it has been submitted
                successfully.
              </p>
              <div className='polymer-stage-d-valuation-fee__tnc-checkbox'>
                <P2Checkbox
                  isActive={tncAccepted}
                  onClick={() => setTncAccepted((prev) => !prev)}
                  description={
                    <p className='m-0 polymer-main-column-tnc-checkbox'>
                      I have read and understood the above and agreed to the{' '}
                      <a className='polymer-text-links' href={`${process.env.PUBLIC_URL}/documents/Terms & Conditions for Ownership(Apr 2026).pdf`}>
                        Terms & Conditions for the change in Flat Ownership
                      </a>
                      .
                    </p>
                  }
                />
              </div>
            </div>
          </div>
        </div>
      </div>
      <div className='polymer-stage-d-valuation-fee__cta'>
        <Separator />
        <div className='polymer-stage-d-valuation-fee__cta-buttons'>
          <P2ButtonGroup>
            <P2Button variant='outline-primary' onClick={onCancel}>
              Cancel
            </P2Button>
            <div className='polymer-stage-d-valuation-fee__continue'>
              <P2Button variant='primary-1' onClick={() => void pay()} disabled={!continueEnabled}>
                {loading ? 'Processing…' : 'Continue'}
              </P2Button>
            </div>
            {!isProd ? (
              <div className='polymer-stage-d-valuation-fee__continue'>
                <P2Button variant='primary-1' onClick={() => void byPassPayment()} disabled={!continueEnabled}>
                  {loading ? 'Processing…' : 'ByPass Payment'}
                </P2Button>
              </div>
            ) : null}
          </P2ButtonGroup>
        </div>
      </div>
      <form ref={paymentFormRef} method='POST' action={paymentUrl}>
        <input type='hidden' name='ttcNumPayReq' id='vf-ttcNumPayReq' value='' />
        <input type='hidden' name='systIdforPaymentPayReq' id='vf-systIdforPaymentPayReq' value='BR20' />
        <input type='hidden' name='systIdTagPayReq' id='vf-systIdTagPayReq' value='' />
      </form>
      <P2Modal
        isOpen={paymentStatusModalOpen}
        onCloseModal={() => setPaymentStatusModalOpen(false)}
        variant='default'
        title='Payment Status'
        primaryButtonText='OK'
        primaryButtonFunction={() => setPaymentStatusModalOpen(false)}
        children={<p style={{ marginBottom: '32px' }}>{paymentStatusMessage}</p>}
      />
    </div>
  );
}
